import { dv } from '@aviv-estimation-funnel/data-validation/dv';
import type { FieldValidator } from '@aviv-estimation-funnel/data-validation/field-validator';
import { ConstructionYearAndPeriodMismatchFilter } from '@aviv-estimation-funnel/data-validation/filters/construction-year-and-period-mismatch-filter';
import {
    Country,
    EstateType,
    germanEpcEnergyEfficiencyRatings,
    GermanEstateSubTypeApartment,
    GermanEstateSubTypeHouse,
    GermanRegionCodes,
    OwnerObjective,
    SaleMethod,
    WantsToSellOwnerObjectives,
} from '@aviv-estimation-funnel/estimation-data-models/enums';
import { CONSTRUCTION_PERIODS_PER_COUNTRY } from '@aviv-estimation-funnel/estimation-data-models/models';

import { germanPostalCodeLength } from '../constants';

import {
    commonNewEstimationFormFieldsValidators,
    commonRegularExpressions,
    commonRenewValuationFormFieldsValidators,
} from './common.config';

export const germanRegularExpressions = {
    ...commonRegularExpressions,
    // 5 digits
    PostalCode: new RegExp(`^[0-9]{${germanPostalCodeLength}}$`),
};

export const germanNewEstimationFormFieldsValidators: Record<string, FieldValidator<unknown>> = {
    ...commonNewEstimationFormFieldsValidators,

    // Step 1 - Location
    postalCode: dv.string().format(germanRegularExpressions.PostalCode),
    houseNumber: dv.string().optional(),

    // Step 3 - Characteristics
    estateSubType: dv
        .string()
        .conditionalFiltering(
            (fieldValues) => fieldValues['estateType'] === EstateType.Apartment,
            [dv.filters.enum(GermanEstateSubTypeApartment)],
            [dv.filters.enum(GermanEstateSubTypeHouse)],
        ),
    numberOfRooms: dv.float().range(0.5, 100).multipleOf(0.5),

    // Step 5 - Conditions
    epcEnergyEfficiencyRating: dv.enum(germanEpcEnergyEfficiencyRatings).optional(),
    constructionYear: dv
        .integer()
        .optional()
        .custom(
            new ConstructionYearAndPeriodMismatchFilter(
                CONSTRUCTION_PERIODS_PER_COUNTRY[Country.Germany],
            ),
        )
        .range(1_000, new Date().getUTCFullYear()),

    // Step 6 - Your profile
    saleMethod: dv
        .enum(SaleMethod)
        .optional() // TODO: VEGA-1446 remove this optional statement
        .if((fieldValues) =>
            WantsToSellOwnerObjectives.includes(fieldValues['ownerObjective'] as OwnerObjective),
        ),
    phoneNumberRegionCode: dv.enum(GermanRegionCodes),
    phoneNumber: dv
        .phoneNumber('phoneNumberRegionCode')
        .regionCodeIn(
            // We validate the Country code again because the phoneNumber could be +33612345678 which
            // contains a country code by itself. We want to avoid to transform the given phone number
            // by magically changing the country code if there is one, so we raise an error.
            GermanRegionCodes,
        )
        .mobile(),
};

export const germanRenewFormFieldsValidator: Record<string, FieldValidator<unknown>> = {
    ...commonRenewValuationFormFieldsValidators,
    // saleMethod: germanNewEstimationFormFieldsValidators['saleMethod'],

    phoneNumberRegionCode: dv.enum(GermanRegionCodes).optional(),
    phoneNumber: dv
        .phoneNumber('phoneNumberRegionCode')
        .if((fieldValues) => fieldValues['phoneNumberRegionCode'] !== undefined)
        .regionCodeIn(
            // We validate the Country code again because the phoneNumber could be +33612345678 which
            // contains a country code by itself. We want to avoid to transform the given phone number
            // by magically changing the country code if there is one, so we raise an error.
            GermanRegionCodes,
        )
        .mobile()
        .optional(),
};
